/**
 * Every owner account gets a billing entity (COL-486).
 *
 * An owner exists twice over — the `lessors` row we invoice, and the portal
 * account that logs in (see `@/lib/owner-directory`). Creating only the account
 * left the owner unbillable, so every screen that makes someone an owner calls
 * this to make sure the billing half exists too.
 *
 * WHY THIS IS A BILLING DECISION, NOT A CONVENIENCE: the
 * `apartments_fill_lessor_from_owner` trigger fills a blank
 * `apartments.lessor_id` from the owner account's lessor whenever the
 * apartment's `owner_user_id` is written, and `generate_management_invoices`
 * bills `COALESCE(apartments.lessor_id, properties.lessor_id)`. So the lessor
 * this resolves to is who that owner's apartments get invoiced to from then on.
 * That is why callers let the admin pick one explicitly (a representative may
 * sign for a company that is the real bill-to), and only fall back to this —
 * reuse the email's lessor, else create one — when nothing was picked. It is
 * also why it runs when someone BECOMES an owner, never on a routine edit of an
 * existing one: that would move invoices nobody asked to move.
 */
import { supabase } from "@/lib/supabase";

/** What we know about the owner when their billing entity has to be created. */
export type OwnerLessorSeed = {
  name: string;
  email: string;
  /**
   * The property the owner is being attached to. Its operator scopes both the
   * email lookup and the insert: staff RLS sees every operator's lessors, so an
   * unscoped match could hand this owner another operator's billing record,
   * and an unscoped insert would be filed under the platform operator
   * (set_operator_id_from_caller) — invisible to the operator it bills for.
   */
  propertyId?: number | string | null;
  phone?: string | null;
  bankHolder?: string | null;
  bankIban?: string | null;
  bankBic?: string | null;
  bankName?: string | null;
};

/** The table/RPC calls this needs — narrow so tests can stub them. */
type LessorsClient = {
  from: (
    table:
      | "lessors"
      | "profiles"
      | "user_roles"
      | "property_owners"
      | "properties"
  ) => any;
  rpc?: (fn: "admin_delete_lessor", args: Record<string, unknown>) => any;
};

/**
 * A billing-entity step that could not complete for a reason the admin can act
 * on. Callers map `code` to translated copy; the message is for logs only.
 */
export class OwnerLessorError extends Error {
  constructor(
    readonly code: "ambiguous" | "not_created" | "wrong_operator",
    message: string
  ) {
    super(message);
    this.name = "OwnerLessorError";
  }
}

/**
 * `ilike` takes a PATTERN, so an address containing % or _ would match other
 * people's records ("a_b@x.com" matching "axb@x.com"). Escape the wildcards.
 */
export function escapeIlikePattern(value: string): string {
  return value.replace(/[\\%_]/g, "\\$&");
}

/** The operator a property belongs to, or null when unknown / no property. */
export async function resolvePropertyOperatorId(
  propertyId: number | string | null | undefined,
  db: LessorsClient = supabase as unknown as LessorsClient
): Promise<string | null> {
  if (propertyId == null || propertyId === "") return null;
  const { data, error } = await db
    .from("properties")
    .select("operator_id")
    .eq("id", Number(propertyId))
    .maybeSingle();
  if (error) throw error;
  return (data?.operator_id as string | null | undefined) ?? null;
}

/**
 * The lessor already on file for this email (within `operatorId` when known),
 * or null when there is none.
 *
 * TWO matches throw `ambiguous` instead of picking the oldest: the attribution
 * trigger deliberately refuses to guess between lessors sharing an email, and
 * writing one as the account's explicit link would settle that coin flip for
 * it — who gets invoiced is the admin's call. A failed lookup THROWS rather
 * than reading as "none", which would create a duplicate billing entity.
 */
export async function findOwnerLessorIdByEmail(
  email: string,
  operatorId: string | null,
  db: LessorsClient = supabase as unknown as LessorsClient
): Promise<string | null> {
  let q = db
    .from("lessors")
    .select("id")
    .ilike("email", escapeIlikePattern(email.trim()));
  if (operatorId) q = q.eq("operator_id", operatorId);
  const { data, error } = await q
    .order("created_at", { ascending: true })
    .order("id", { ascending: true })
    .limit(2);
  if (error) throw error;
  const rows = (data ?? []) as { id: string }[];
  if (rows.length > 1)
    throw new OwnerLessorError(
      "ambiguous",
      "Several billing entities share this email."
    );
  return rows[0]?.id ?? null;
}

/**
 * Resolve the owner's billing entity: the one the admin picked, else the one on
 * file for their email (in the property's operator), else a new one made from
 * `seed` and filed under that operator.
 *
 * Throws (Supabase error or OwnerLessorError) when it cannot, so the caller can
 * stop before creating an account that has no billing half.
 */
export async function ensureOwnerLessor(
  seed: OwnerLessorSeed,
  pickedLessorId: string | null,
  db: LessorsClient = supabase as unknown as LessorsClient
): Promise<{ lessorId: string; created: boolean }> {
  const operatorId = await resolvePropertyOperatorId(seed.propertyId, db);

  if (pickedLessorId) {
    // A pick — or "Create login"'s preselected row — still has to live in the
    // property's operator: staff see every operator's lessors in the picker,
    // and a cross-operator link would invoice one operator's apartments to
    // another's billing entity.
    if (operatorId) {
      const { data: picked, error: pickErr } = await db
        .from("lessors")
        .select("operator_id")
        .eq("id", pickedLessorId)
        .maybeSingle();
      if (pickErr) throw pickErr;
      if (picked && picked.operator_id && picked.operator_id !== operatorId)
        throw new OwnerLessorError(
          "wrong_operator",
          "The picked billing entity belongs to another operator."
        );
    }
    return { lessorId: pickedLessorId, created: false };
  }

  // No email, nothing to match on — an empty pattern would only ever find
  // other email-less rows, which are not this owner.
  const email = seed.email.trim();
  const existing = email
    ? await findOwnerLessorIdByEmail(email, operatorId, db)
    : null;
  if (existing) return { lessorId: existing, created: false };

  const blank = (v: string | null | undefined) => v?.trim() || null;
  const { data, error } = await db
    .from("lessors")
    .insert({
      name: seed.name.trim() || email || "Unnamed owner",
      type: "Individual",
      email: email || null,
      phone: blank(seed.phone),
      bank_holder: blank(seed.bankHolder),
      bank_iban: blank(seed.bankIban),
      bank_bic: blank(seed.bankBic),
      bank_name: blank(seed.bankName),
      // Explicit, so a staff caller files it under the operator it bills for.
      ...(operatorId ? { operator_id: operatorId } : {}),
    })
    .select("id")
    .single();
  if (error) throw error;
  if (!data?.id)
    throw new OwnerLessorError("not_created", "Lessor insert returned no id.");
  return { lessorId: String(data.id), created: true };
}

/**
 * The OWNER account behind this email, or null when there is none.
 *
 * Add owner and the Invite panel can both be run for an existing account. For
 * someone who is already an owner they must not mint a billing entity: their
 * apartments are invoiced to whoever they resolve to today (usually the
 * property's lessor), and a new link would move those invoices on the next
 * assignment save. Filling that gap is a deliberate "Add billing entity", not
 * a side effect. Throws on a failed read for the same reason
 * findOwnerLessorIdByEmail does.
 */
export async function findExistingOwnerAccountId(
  email: string,
  db: LessorsClient = supabase as unknown as LessorsClient
): Promise<string | null> {
  const { data: profiles, error } = await db
    .from("profiles")
    .select("id")
    .ilike("email", escapeIlikePattern(email.trim()));
  if (error) throw error;
  const ids = ((profiles ?? []) as { id: string }[]).map((p) => p.id);
  if (ids.length === 0) return null;
  const { data: roles, error: rolesErr } = await db
    .from("user_roles")
    .select("user_id")
    .in("user_id", ids)
    .eq("role", "owner")
    .limit(1);
  if (rolesErr) throw rolesErr;
  return ((roles ?? []) as { user_id: string }[])[0]?.user_id ?? null;
}

/**
 * The billing entity an existing owner is ALREADY explicitly linked to on THIS
 * property, to carry forward on a re-add — or null when there is none.
 *
 * admin-invite-user upserts `lessor_id` onto the (user, property) row and
 * writes an omitted value as NULL. So "leave their billing alone" has to
 * re-send that row's current link, not nothing. Only this property's link:
 * it is the only row the upsert touches (their other links are untouched),
 * and borrowing a link from another property could carry a lessor across
 * operators onto this one.
 */
export async function existingOwnerLessorId(
  userId: string,
  propertyId: number | string | null | undefined,
  db: LessorsClient = supabase as unknown as LessorsClient
): Promise<string | null> {
  if (propertyId == null || propertyId === "") return null;
  const { data, error } = await db
    .from("property_owners")
    .select("lessor_id")
    .eq("user_id", userId)
    .eq("property_id", Number(propertyId))
    .maybeSingle();
  if (error) throw error;
  return (data?.lessor_id as string | null | undefined) ?? null;
}

/**
 * The billing step for Add owner and the Invite panel: the lessor to send as
 * `lessor_id` (and whether this call created it), or undefined when there is
 * nothing to send.
 *
 * A pick always wins. A NEW owner gets one (email match, else created). An
 * account that is already an owner keeps the link it has (never a new one).
 */
export async function resolveNewOwnerLessor(
  seed: OwnerLessorSeed,
  pickedLessorId: string | null,
  db: LessorsClient = supabase as unknown as LessorsClient
): Promise<{ lessorId: string; created: boolean } | undefined> {
  if (!pickedLessorId) {
    const ownerId = await findExistingOwnerAccountId(seed.email, db);
    if (ownerId) {
      const kept = await existingOwnerLessorId(ownerId, seed.propertyId, db);
      return kept ? { lessorId: kept, created: false } : undefined;
    }
  }
  return ensureOwnerLessor(seed, pickedLessorId, db);
}

/**
 * Undo a billing entity THIS attempt created, after the account half failed —
 * so a failed invite leaves nothing behind but the error.
 *
 * Only ever called with an id `ensureOwnerLessor` reported as `created`; a
 * reused lessor must never be deleted. Only while nothing links to it: a
 * network error can hide an invite that DID land server-side and attached the
 * lessor to the new account's property_owners row — that FK is ON DELETE SET
 * NULL, so deleting then would silently unlink a real owner from billing.
 * Goes through `admin_delete_lessor` (clients have no DELETE on lessors; the
 * RPC keeps the audit trail). Best-effort: when in doubt, or for a caller the
 * RPC refuses, the row stays as an ordinary lessor an admin can remove, and a
 * retry reuses it by email rather than creating another.
 */
export async function discardCreatedOwnerLessor(
  lessorId: string,
  db: LessorsClient = supabase as unknown as LessorsClient
): Promise<boolean> {
  if (!db.rpc) return false;
  // The "is anything linked to it" check happens atomically INSIDE the RPC
  // (see admin_delete_lessor's `_only_if_unreferenced`), not as a separate
  // client round trip a concurrent write (e.g. an onboarding sign-up
  // resolving to this same lessor) could race.
  const { error } = await db.rpc("admin_delete_lessor", {
    _lessor_id: lessorId,
    _reason:
      "Owner invite failed — removing the billing entity created for it.",
    _only_if_unreferenced: true,
  });
  if (error) {
    console.warn("[ensure-owner-lessor] discard failed:", error);
    return false;
  }
  return true;
}

/**
 * The Edit user dialog's billing step: the lessor to save as the assignment's
 * `_lessor_id`.
 *
 * Only someone BECOMING an owner gets one ensured. For an account that already
 * was an owner (or is not one now) the dialog's own selection passes through
 * untouched — a routine edit must not mint a lessor and move invoices.
 *
 * `admin_replace_owner_assignments` writes the one lessor onto EVERY assigned
 * property, so a new owner's assignments must all sit in one operator — a
 * single billing entity cannot be invoiced across operators.
 */
export async function resolveEditedOwnerLessor(
  change: { role: string; wasOwner: boolean },
  seed: OwnerLessorSeed,
  pickedLessorId: string | null,
  assignmentPropertyIds: readonly (number | string)[] = [],
  db: LessorsClient = supabase as unknown as LessorsClient
): Promise<string | null> {
  if (change.role !== "owner" || change.wasOwner) return pickedLessorId;
  const operators = new Set(
    (
      await Promise.all(
        assignmentPropertyIds.map((id) => resolvePropertyOperatorId(id, db))
      )
    ).filter((op): op is string => op != null)
  );
  if (operators.size > 1)
    throw new OwnerLessorError(
      "wrong_operator",
      "Assignments span several operators."
    );
  return (
    await ensureOwnerLessor(
      { ...seed, propertyId: assignmentPropertyIds[0] ?? seed.propertyId },
      pickedLessorId,
      db
    )
  ).lessorId;
}
